import {AggregationType, ChartOutput, ChartQuery} from 'entities/chart';
import {
    ChartType,
    chartOutputTypeChoices,
    hierarchicalAggregationChartTypes,
} from '@/views/storyboard/utils/chartConstants';
import {chartQueryDefaultProps} from '@/views/storyboard/utils/chartPropTypes';
import {showsPeriodSection} from '@/views/storyboard/utils/chartSettingsFieldVisibility';

export function sanitizeChartQueryForRequest(chartQuery: ChartQuery, chartType: ChartType): ChartQuery {
    const sanitized: ChartQuery = {...chartQuery};
    const aggsItemListLength = sanitized.aggs_item_list?.length ?? 0;

    if (
        hierarchicalAggregationChartTypes.includes(chartType)
        && (
            sanitized.output === ChartOutput.MultiTerms
            || sanitized.output === ChartOutput.NestedTerms
        )
        && aggsItemListLength >= 2
    ) {
        sanitized.output = ChartOutput.NestedTerms;
    }

    const output = sanitized.output;

    if (
        output === ChartOutput.MultiTerms
        || output === ChartOutput.NestedTerms
        || output === ChartOutput.MultiTermsTimeline
    ) {
        sanitized.aggs_item = null;
        sanitized.metric_aggregations_aggs_item = null;
        sanitized.pivot_col_aggs_item = null;
    } else {
        sanitized.aggs_item_list = [];
    }

    if (output !== ChartOutput.PivotTerms) {
        sanitized.pivot_col_aggs_item = null;
    }

    if (
        sanitized.metric_aggregations_type === AggregationType.Count
        || !sanitized.metric_aggregations_aggs_item
    ) {
        sanitized.metric_aggregations_aggs_item = null;
    }

    if (output !== ChartOutput.NestedTerms) {
        delete (sanitized as Partial<ChartQuery>).nested_terms_include_missing;
    }

    return sanitized;
}

const QUERY_TYPES_WITHOUT_AGG_FIELDS: ChartType[] = [
    ChartType.Wordcloud,
    ChartType.TextSamples,
    ChartType.Concordance,
    ChartType.TrendingWords,
];

export function showsMetricTimelineTypeField(chartType: ChartType, output: ChartOutput | undefined): boolean {
    return output === ChartOutput.MetricTimeline
        && !QUERY_TYPES_WITHOUT_AGG_FIELDS.includes(chartType);
}

export function showsMetricAggregationsCrossField(
    chartType: ChartType,
    output: ChartOutput | undefined,
): boolean {
    return (chartType === ChartType.Table || chartType === ChartType.BarChart)
        && output === ChartOutput.Terms;
}

export function showsMetricAggregationsTypeField(
    chartType: ChartType,
    output: ChartOutput | undefined,
    metricAggregationsAggsItem: string | null,
): boolean {
    if (QUERY_TYPES_WITHOUT_AGG_FIELDS.includes(chartType)) {
        return false;
    }

    if (output === ChartOutput.TermsMetric) {
        return true;
    }

    if (!showsMetricAggregationsCrossField(chartType, output)) {
        return false;
    }

    return !!metricAggregationsAggsItem
        && metricAggregationsAggsItem !== '-'
        && metricAggregationsAggsItem !== '';
}

export function showsMetricIntervalField(chartQuery: ChartQuery): boolean {
    return (
        chartQuery.metric_timeline_type === AggregationType.Histogram
        && chartQuery.output === ChartOutput.MetricTimeline
    ) || (
        chartQuery.output === ChartOutput.TermsMetric
        && chartQuery.metric_aggregations_type === AggregationType.Histogram
    );
}

export function cleanupTypeSpecificQueryFields(chartQuery: ChartQuery, chartType: ChartType): ChartQuery {
    const defaults = chartQueryDefaultProps;
    const query: ChartQuery = {...chartQuery};

    if (chartType !== ChartType.Wordcloud && chartType !== ChartType.TrendingWords) {
        query.n_gram = defaults.n_gram;
        query.filtering_lists = [...defaults.filtering_lists];
        delete query.wordcloud_most_common_words;
        delete query.wordcloud_text_start_point;
        delete query.wordcloud_text_end_point;
    }

    if (chartType !== ChartType.Concordance) {
        query.concordance_query = defaults.concordance_query;
        query.concordance_width = defaults.concordance_width;
        query.concordance_lines = defaults.concordance_lines;
    }

    if (chartType !== ChartType.TrendingWords) {
        query.trending_comparable_period = defaults.trending_comparable_period;
        query.trending_period = defaults.trending_period;
        query.trending_or_disappearing = defaults.trending_or_disappearing;
    }

    if (!showsPeriodSection(chartType, query.output)) {
        query.intervalUnit = defaults.intervalUnit;
        query.intervalNumber = defaults.intervalNumber;
    }

    return query;
}

export function cleanupMetricQueryFields(chartQuery: ChartQuery, chartType: ChartType): ChartQuery {
    const defaults = chartQueryDefaultProps;
    const query: ChartQuery = {...chartQuery};

    if (chartType === ChartType.PivotTable) {
        query.output = ChartOutput.PivotTerms;
        query.metric_aggregations_type = AggregationType.Count;
        query.metric_aggregations_aggs_item = null;
        query.aggs_item_list = [];
        return query;
    }

    if (!showsMetricTimelineTypeField(chartType, query.output)) {
        query.metric_timeline_type = defaults.metric_timeline_type;
    }

    const showsCrossField = showsMetricAggregationsCrossField(chartType, query.output);
    const showsMetricTypeField = showsMetricAggregationsTypeField(
        chartType,
        query.output,
        query.metric_aggregations_aggs_item,
    );

    if (!showsCrossField && !showsMetricTypeField) {
        query.metric_aggregations_aggs_item = null;
    }

    if (!showsMetricTypeField) {
        query.metric_aggregations_type = defaults.metric_aggregations_type;
    }

    if (!showsMetricIntervalField(query)) {
        query.metric_interval = defaults.metric_interval;
    }

    return query;
}

export function resolveOutputForChartType(
    chartType: ChartType,
    currentOutput: ChartOutput | undefined,
): ChartOutput | undefined {
    if (chartType === ChartType.PivotTable) {
        return ChartOutput.PivotTerms;
    }

    const outputOptions = chartOutputTypeChoicesForType(chartType);
    if (!outputOptions.length) {
        return undefined;
    }

    if (currentOutput && outputOptions.includes(currentOutput)) {
        return currentOutput;
    }

    if (
        currentOutput === ChartOutput.Terms
        && outputOptions.includes(ChartOutput.TermsTimeline)
    ) {
        return ChartOutput.TermsTimeline;
    }

    if (
        (currentOutput === ChartOutput.MultiTerms || currentOutput === ChartOutput.NestedTerms)
        && outputOptions.includes(ChartOutput.MultiTermsTimeline)
    ) {
        return ChartOutput.MultiTermsTimeline;
    }

    if (
        currentOutput === ChartOutput.MultiTermsTimeline
        && outputOptions.includes(ChartOutput.MultiTerms)
    ) {
        return ChartOutput.MultiTerms;
    }

    if (
        (currentOutput === ChartOutput.TermsTimeline || currentOutput === ChartOutput.MultiTermsTimeline)
        && outputOptions.includes(ChartOutput.Terms)
    ) {
        return ChartOutput.Terms;
    }

    return outputOptions[0];
}

function chartOutputTypeChoicesForType(chartType: ChartType): ChartOutput[] {
    return chartOutputTypeChoices[chartType] ?? [];
}
